import React, { useId } from "react"
import { cn } from "../utils"
import { VisuallyHidden } from "../visually-hidden"
import { PageSecondaryHeaderCenterContent } from "./page-secondary-header-center-content"
import { PageSecondaryHeaderOverflowScroll } from "./page-secondary-header-overflow-scroll"
import { pageSecondaryHeaderTabContainerStyles } from "./page-secondary-header-tab-container-styles"

// =============================================================================
// Props
// =============================================================================

export type PageSecondaryHeaderTabLinksProps = {
  accessibleName: string
  children?: React.ReactNode
  className?: string
}

// =============================================================================
// Styles
// =============================================================================

const pageSecondaryHeaderTabLinksStyles = ["flex items-center gap-6 w-max"]

// =============================================================================
// Component
// =============================================================================

/**
 * Secondary page header tab links (cross-page navigation).
 *
 * Renders a `<nav>` landmark for navigation that *visually* looks like tabs but
 * moves the user between pages (each link has its own URL). Compose with
 * `PageSecondaryHeader.TabLink` for each link; the active link is derived from
 * the ancestor `PageHeader`'s `currentPath`.
 *
 * For in-page panel switching (same URL, switching content), use
 * `PageSecondaryHeader.Tabs` instead — it renders a real `role="tablist"` with
 * arrow-key navigation and shared state via `PageSecondaryHeader.TabsRoot`.
 *
 * The accessible name differentiates this navigation landmark from the main
 * `<Navigation>` component (e.g. "Broadcast sections").
 */
export function PageSecondaryHeaderTabLinks({
  accessibleName,
  children,
  className
}: PageSecondaryHeaderTabLinksProps) {
  const accessibleNameId = useId()

  return (
    <PageSecondaryHeaderCenterContent>
      <PageSecondaryHeaderOverflowScroll
        className={cn(pageSecondaryHeaderTabContainerStyles)}
        render={
          <nav
            aria-labelledby={accessibleNameId}
            data-slot="secondary-tab-links"
          />
        }
      >
        <VisuallyHidden id={accessibleNameId}>{accessibleName}</VisuallyHidden>
        <div className={cn(pageSecondaryHeaderTabLinksStyles, className)}>
          {children}
        </div>
      </PageSecondaryHeaderOverflowScroll>
    </PageSecondaryHeaderCenterContent>
  )
}
